import { z } from 'zod';
import { isoDateSchema, isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Year-end: the accountant's deliverable. T5018 slips, Schedule 8 capital
 * cost allowance, the close, and the working-papers package. Money is a
 * string of cents throughout; rates and factors are decimal strings.
 */
const minor = z.string().regex(/^-?\d{1,15}$/);
export const fiscalYearStatusSchema = z.object({
    fiscalYear: z.number().int(),
    startOn: isoDateSchema,
    endOn: isoDateSchema,
    fiscalYearEndMonth: z.number().int().min(1).max(12),
    /** Whether the year's last day is behind us. A year is closed only after it ends. */
    ended: z.boolean(),
    closed: z.object({ closedAt: isoDateTimeSchema, entryId: uuidSchema.nullable() }).nullable(),
    lastReopen: z.object({ reopenedAt: isoDateTimeSchema, reason: z.string().nullable() }).nullable(),
    ccaEntryId: uuidSchema.nullable(),
    checklist: z.object({
        openMonths: z.number().int(),
        monthsDefined: z.number().int(),
        draftsInYear: z.number().int(),
        unmatchedBankLines: z.number().int(),
        openReviewItems: z.number().int(),
        unfiledHstPeriods: z.number().int(),
        t5018MissingDetails: z.number().int(),
    }),
});
export const setFiscalYearEndRequestSchema = z.object({ month: z.number().int().min(1).max(12) });
export const t5018RowSchema = z.object({
    vendorId: uuidSchema,
    displayName: z.string(),
    legalName: z.string().nullable(),
    /** The nine-digit business number, taken from the GST/HST number on file. */
    businessNumber: z.string().nullable(),
    mailingAddress: z.string().nullable(),
    paidMinor: minor,
    /** At or above the $500 threshold for the year. */
    reportable: z.boolean(),
    missing: z.array(z.string()),
});
export const ccaRowSchema = z.object({
    classId: uuidSchema,
    classCode: z.string(),
    description: z.string(),
    rate: z.string(),
    halfYearApplies: z.boolean(),
    openingMinor: minor,
    additionsMinor: minor,
    dispositionsMinor: minor,
    /** 1 while the accelerated investment incentive suspends the half-year rule; 0.5 when it applies. */
    firstYearFactor: z.string(),
    maxClaimMinor: minor,
    claimMinor: minor,
    closingMinor: minor,
    recaptureMinor: minor,
    hasInput: z.boolean(),
});
export const ccaInputRequestSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100),
    classId: uuidSchema,
    openingMinor: z.string().regex(/^\d{1,15}$/),
    dispositionsMinor: z.string().regex(/^\d{1,15}$/),
    /** Null claims the maximum. */
    claimMinor: z
        .string()
        .regex(/^\d{1,15}$/)
        .nullable(),
});
export const fiscalYearRequestSchema = z.object({
    fiscalYear: z.number().int().min(2000).max(2100),
});
export const reopenYearRequestSchema = fiscalYearRequestSchema.extend({
    reason: z.string().trim().min(5).max(500),
});
//# sourceMappingURL=year-end.js.map